// src/features/softwareLicenses/hooks/useSoftwareLicenses.js
import { useState, useCallback } from 'react';
import { softwareLicensesAPI } from '../services/softwareLicenses';
import { useApi } from '../../../hooks/api/useApi';

export function useSoftwareLicenses() {
  const [licenses, setLicenses] = useState([]);
  const [currentLicense, setCurrentLicense] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const { loading, error, execute, clearError } = useApi();

  const fetchAllLicenses = useCallback(async (params = {}) => {
    const result = await execute(() => softwareLicensesAPI.getAllLicenses(params));
    console.log(result)
    if (result.success) {
      setLicenses(result.data.data.licenses || []);
    }
    return result;
  }, [execute]);

  const fetchUnactivatedLicenses = useCallback(async (params = {}) => {
    const result = await execute(() => softwareLicensesAPI.getUnactivatedLicenses(params));
    if (result.success) {
      setLicenses(result.data.data.licenses || []);
    }
    return result;
  }, [execute]);

  const fetchDepartmentLicenses = useCallback(async (departmentId, params = {}) => {
    const result = await execute(() => softwareLicensesAPI.getDepartmentLicenses(departmentId, params));
    if (result.success) {
      setLicenses(result.data.licenses || []);
    }
    return result;
  }, [execute]);

  const fetchLicenseById = useCallback(async (licenseId) => {
    const result = await execute(() => softwareLicensesAPI.getLicenseById(licenseId));
    if (result.success) {
      setCurrentLicense(result.data.data);
    }
    return result;
  }, [execute]);

  const activateLicense = useCallback(async (licenseId, departmentId) => {
    const result = await execute(() => 
      softwareLicensesAPI.activateLicense(licenseId, { department_id: departmentId })
    );
    if (result.success) {
      // Update license in list
      setLicenses(prev => prev.map(lic => 
        lic.license_id === licenseId 
          ? { ...lic, is_activated: true, department_id: departmentId } 
          : lic
      ));
      // Update current license if viewing
      if (currentLicense?.license_id === licenseId) {
        setCurrentLicense(prev => ({ 
          ...prev, 
          is_activated: true, 
          department_id: departmentId 
        }));
      }
    }
    return result;
  }, [execute, currentLicense]);

  const deactivateLicense = useCallback(async (licenseId) => {
    const result = await execute(() => softwareLicensesAPI.deactivateLicense(licenseId));
    if (result.success) {
      // Update license in list
      setLicenses(prev => prev.map(lic => 
        lic.license_id === licenseId 
          ? { ...lic, is_activated: false, department_id: null } 
          : lic
      ));
      // Update current license if viewing
      if (currentLicense?.license_id === licenseId) {
        setCurrentLicense(prev => ({ 
          ...prev, 
          is_activated: false, 
          department_id: null 
        }));
      }
    }
    return result;
  }, [execute, currentLicense]);

  const deleteLicense = useCallback(async (licenseId) => {
    const result = await execute(() => softwareLicensesAPI.deleteLicense(licenseId));
    if (result.success) {
      setLicenses(prev => prev.filter(lic => lic.license_id !== licenseId));
      if (currentLicense?.license_id === licenseId) {
        setCurrentLicense(null);
      }
    }
    return result;
  }, [execute, currentLicense]);

  const fetchStatistics = useCallback(async () => {
    const result = await execute(() => softwareLicensesAPI.getStatistics());
    if (result.success) {
      setStatistics(result.data);
    }
    return result;
  }, [execute]);

  return {
    licenses,
    currentLicense,
    statistics,
    loading,
    error,
    clearError,
    fetchAllLicenses,
    fetchUnactivatedLicenses,
    fetchDepartmentLicenses,
    fetchLicenseById,
    activateLicense,
    deactivateLicense,
    deleteLicense,
    fetchStatistics,
  };
}